// CHANGE-073: the partners a company can use, once it has sent its card request. The list is partners.ts and nothing
// else: no server, no points. A featured partner shows in the top row only. Only in the card-only app; with it off, /partners stays the partner sign-in short link.
import { Navigate } from 'react-router-dom'
import { useCurrentCompany, useMe } from '../../lib'
import { Loading } from '../../app/ui'
import { PartnersShortLink } from '../accounts/LoginPage'
import { useCardOverview } from './CardPages'
import { GROUPS, PARTNERS, type Partner } from './partners'
import './partners.css'

/** true when /partners is open to this user: staff, or a company whose card request is sent. undefined while it loads. */
export function usePartnersOpen(): boolean | undefined {
  const { me } = useMe()
  const company = useCurrentCompany()
  const q = useCardOverview(company?.id, me?.features?.card === true && !me.is_staff)
  if (!me) return undefined
  if (!me.user || !me.features?.card_only_app || PARTNERS.length === 0) return false
  if (me.is_staff) return true
  if (!company || !me.features?.card) return false
  if (q.isLoading) return undefined
  const status = q.data?.request?.status
  return status === 'received' || status === 'invited'
}

export function PartnersPage() {
  const { me } = useMe()
  const open = usePartnersOpen()
  if (me && !me.features?.card_only_app) return <PartnersShortLink />
  if (open === undefined) return <Loading />
  if (!open) return <Navigate to="/card" replace />
  const featured = PARTNERS.filter((p) => p.featured)
  return (
    <div className="page partners" data-testid="partners-page">
      <h1 className="partners-title">Partners</h1>
      {featured.length ? (
        <section className="partners-group" data-testid="partners-featured">
          <h2>Featured</h2>
          <List partners={featured} />
        </section>
      ) : null}
      {GROUPS.map((g) => {
        const list = PARTNERS.filter((p) => p.group === g && !p.featured)
        return list.length ? (
          <section key={g} className="partners-group" data-testid={`partners-group-${g.toLowerCase().replace(/\s+/g, '-')}`}>
            <h2>{g}</h2>
            <List partners={list} />
          </section>
        ) : null
      })}
    </div>
  )
}

function List({ partners }: { partners: Partner[] }) {
  return (
    <ul className="partners-list">
      {partners.map((p) => (
        <li key={p.name}>
          <a className="partner-card" href={p.url} target="_blank" rel="noopener noreferrer" data-testid="partner-card">
            {p.logo ? <img className="partner-logo" src={p.logo} alt="" />
              : <span className="partner-logo partner-letter" aria-hidden="true">{p.name.trim().charAt(0).toUpperCase()}</span>}
            <span className="partner-text">
              <span className="partner-name">{p.name}</span>
              <span className="partner-what">{p.what}</span>
              <span className="partner-tags">
                <span className="partner-discount">{p.discount}</span>
                <span className="partner-group">{p.group}</span>
              </span>
            </span>
          </a>
        </li>
      ))}
    </ul>
  )
}
